package org.dronda.app.web.ui.locationconfig

import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import app.cash.molecule.RecompositionMode
import app.cash.molecule.launchMolecule
import com.github.michaelbull.result.onOk
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch
import org.dronda.app.web.repository.LocationSharingRepository
import org.dronda.app.web.repository.UserRepository
import org.dronda.app.web.service.OwntracksConfigurationService
import org.dronda.app.web.ui.util.collectEventSink
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

@OptIn(ExperimentalUuidApi::class)
class LocationConfigViewModel(
    private val userRepository: UserRepository,
    private val owntracksConfigurationService: OwntracksConfigurationService,
    private val locationSharingRepository: LocationSharingRepository,
) {
    private val scope = CoroutineScope(Dispatchers.Main)
    private val eventSink = MutableSharedFlow<Event>(extraBufferCapacity = 8)

    private val mutableLocationSharingFriends = MutableStateFlow<List<LocationSharingFriend>?>(emptyList())

    private fun refreshLocationSharingFriends() {
        scope.launch {
            locationSharingRepository.loadFriends().onOk {
                mutableLocationSharingFriends.value = it.map { LocationSharingFriend(it.id, it.email) }
            }
        }
    }

    val uiState = scope.launchMolecule(mode = RecompositionMode.Immediate) {
        LaunchedEffect(Unit) {
            refreshLocationSharingFriends()
        }

        collectEventSink(eventSink) { event ->
            when (event) {
                is Event.DeleteLocationSharingFriend -> {
                    locationSharingRepository.deleteLocationSharingFriend(userId = event.userId)
                    refreshLocationSharingFriends()
                }
                is Event.OpenOwntracksLink -> {
                    owntracksConfigurationService.openOwntracksLink(
                        userRepository.loggedInState?.username ?: "",
                        event.password
                    )
                }
            }
        }

        val locationSharingFriends by mutableLocationSharingFriends.collectAsState()

        UIState(
            loading = locationSharingFriends == null,
            locationSharingFriends = locationSharingFriends.orEmpty(),
        )
    }

    fun take(event: Event) {
        eventSink.tryEmit(event)
    }

    data class UIState(
        val loading: Boolean,
        val locationSharingFriends: List<LocationSharingFriend>
    )

    sealed interface Event {
        @OptIn(ExperimentalUuidApi::class)
        data class DeleteLocationSharingFriend(
            val userId: Uuid
        ) : Event

        data class OpenOwntracksLink(val password: String) : Event
    }
}

@OptIn(ExperimentalUuidApi::class)
data class LocationSharingFriend(
    val id: Uuid,
    val email: String,
)